import React, { useCallback, useEffect, useState } from "react";

import {
  createApiKey,
  listApiKeys,
  listWorkspaces,
  revokeApiKey,
  type ApiKey,
  type CreatedApiKey,
  type Workspace,
} from "../lib/plbxApi";
import { WorkspaceBadge } from "../components/WorkspaceBadge";

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

export const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [scopeKind, setScopeKind] = useState<"personal" | "workspace">("personal");
  const [scopeWs, setScopeWs] = useState<string>("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listApiKeys();
      setKeys(res.data);
    } catch (e: any) {
      setError(e?.message || "failed to load keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    listWorkspaces()
      .then((res) => {
        setWorkspaces(res.data);
        const firstShared = res.data.find((w) => w.kind === "shared");
        if (firstShared) setScopeWs(firstShared.id);
      })
      .catch((e) => setError(e?.message || "failed to load workspaces"));
  }, [reload]);

  const onCreate = async () => {
    if (!newKeyName.trim()) {
      setError("name required");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await createApiKey({
        name: newKeyName.trim(),
        workspaceId: scopeKind === "workspace" ? scopeWs : null,
      });
      setRevealedKey(created);
      setShowCreate(false);
      setNewKeyName("");
      setScopeKind("personal");
      await reload();
    } catch (e: any) {
      setError(e?.message || "failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const onCopy = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be blocked; user can still select the field
    }
  };

  const onRevoke = async (k: ApiKey) => {
    if (
      !confirm(
        `Revoke API key "${k.name}" (sk-${k.prefix}…)? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await revokeApiKey(k.id);
      setKeys((prev) => prev.filter((x) => x.id !== k.id));
    } catch (e: any) {
      setError(e?.message || "failed to revoke key");
    }
  };

  return (
    <div className="plbx-page plbx-api-keys-page">
      <header className="plbx-page__header">
        <h1>API keys</h1>
        <button
          className="plbx-btn plbx-btn--primary"
          onClick={() => setShowCreate(true)}
        >
          + Create key
        </button>
      </header>

      {error && <div className="plbx-alert plbx-alert--error">{error}</div>}

      {loading ? (
        <div className="plbx-empty">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="plbx-empty">
          No API keys yet. Create one to integrate with the REST API or MCP.
        </div>
      ) : (
        <table className="plbx-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Scope</th>
              <th>Scopes</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td>
                  <code>sk-{k.prefix}…</code>
                </td>
                <td>
                  {(() => {
                    if (!k.workspaceId) return "Personal";
                    const ws = workspaces.find((w) => w.id === k.workspaceId);
                    return ws ? (
                      <WorkspaceBadge slug={ws.slug} label={ws.name} />
                    ) : (
                      <code>{k.workspaceId}</code>
                    );
                  })()}
                </td>
                <td>{k.scopes.join(", ")}</td>
                <td>{fmtDate(k.created_at)}</td>
                <td>{fmtDate(k.last_used_at)}</td>
                <td>{fmtDate(k.expires_at)}</td>
                <td>
                  <button
                    className="plbx-btn plbx-btn--danger"
                    onClick={() => onRevoke(k)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <div
          className="plbx-modal-backdrop"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div className="plbx-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create API key</h2>
            <label className="plbx-field">
              <span>Name</span>
              <input
                className="plbx-input"
                autoFocus
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. claude-code-mcp"
              />
            </label>
            <label className="plbx-field">
              <span>Scope</span>
              <div className="plbx-scope-radio">
                <label className="plbx-radio">
                  <input
                    type="radio"
                    name="scopeKind"
                    checked={scopeKind === "personal"}
                    onChange={() => setScopeKind("personal")}
                  />
                  Personal (only your private boards)
                </label>
                <label className="plbx-radio">
                  <input
                    type="radio"
                    name="scopeKind"
                    checked={scopeKind === "workspace"}
                    onChange={() => setScopeKind("workspace")}
                    disabled={workspaces.filter((w) => w.kind === "shared").length === 0}
                  />
                  Workspace:
                  <select
                    className="plbx-input"
                    value={scopeWs}
                    onChange={(e) => setScopeWs(e.target.value)}
                    disabled={scopeKind !== "workspace"}
                    style={{ marginLeft: 8 }}
                  >
                    {workspaces
                      .filter((w) => w.kind === "shared")
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
            </label>
            <div className="plbx-modal__actions">
              <button
                className="plbx-btn"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="plbx-btn plbx-btn--primary"
                onClick={onCreate}
                disabled={creating}
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {revealedKey && (
        <div className="plbx-modal-backdrop">
          <div className="plbx-modal">
            <h2>Save this key now</h2>
            <p>
              This is the only time the full token will be displayed. Store it
              somewhere safe — you cannot retrieve it later.
            </p>
            <div className="plbx-token-row">
              <code className="plbx-token">{revealedKey.token}</code>
              <button
                className="plbx-btn plbx-btn--primary"
                onClick={onCopy}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="plbx-modal__actions">
              <button
                className="plbx-btn"
                onClick={() => setRevealedKey(null)}
              >
                I have saved it — close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
